import { apiClient, refreshAccessToken, tokenStorage } from './client';
import type { LoginRequest, LoginResponse, LogoutRequest, User } from '@/types/auth';

/**
 * Auth service contract (lets AuthProvider receive a fake in tests)
 */
export interface AuthService {
  login(credentials: LoginRequest): Promise<LoginResponse>;
  /**
   * Refresh the access token; returns the new access token
   */
  refresh(): Promise<string>;
  logout(): Promise<void>;
  getCurrentUser(): Promise<User>;
  /**
   * Whether tokens are stored at all (they may still need a refresh)
   */
  hasStoredSession(): boolean;
  /**
   * Whether the stored access token is still valid
   */
  hasValidAccessToken(): boolean;
  clearAuth(): void;
}

/**
 * Auth API functions for the CDN Manager
 */
export const authApi: AuthService = {
  /**
   * Authenticate user and receive tokens
   */
  async login(credentials) {
    const response = await apiClient.post<LoginResponse>('/auth/login', credentials);

    const { access_token, refresh_token, expires_in } = response.data;

    // Store tokens with expiration timestamp
    tokenStorage.setTokens({
      accessToken: access_token,
      refreshToken: refresh_token,
      expiresAt: Date.now() + expires_in * 1000,
    });

    return response.data;
  },

  /**
   * Refresh the access token using the refresh token.
   * Shares the single refresh implementation with the API client interceptor.
   */
  refresh() {
    return refreshAccessToken();
  },

  /**
   * Logout the current user and invalidate the refresh token
   */
  async logout() {
    const refreshToken = tokenStorage.getRefreshToken();

    if (refreshToken) {
      try {
        const logoutRequest: LogoutRequest = { refresh_token: refreshToken };
        await apiClient.post('/auth/logout', logoutRequest);
      } catch {
        // Ignore logout errors - we'll clear tokens anyway
      }
    }

    // Always clear tokens, regardless of API response
    tokenStorage.clearTokens();
  },

  /**
   * Get the current authenticated user's information
   */
  async getCurrentUser() {
    const response = await apiClient.get<User>('/auth/me');
    return response.data;
  },

  hasStoredSession() {
    return tokenStorage.getTokens() !== null;
  },

  hasValidAccessToken() {
    return tokenStorage.getTokens() !== null && !tokenStorage.isTokenExpired();
  },

  /**
   * Clear all stored auth data (for forced logout scenarios)
   */
  clearAuth() {
    tokenStorage.clearTokens();
  },
};
